import { FieldDefinition } from './field.model';
import {
  ChecklistItem,
  ChecklistItemField,
  ChecklistItemCustomInput,
} from '../../checklist/types/ChecklistTypes';
import { ChecklistStatus } from '../../checklist/types/ChecklistStatus.enum';
import { ChecklistItemStatus } from '../../checklist/types/ChecklistItemStatus.enum';
import { ChecklistFieldType } from '../../checklist/types/ChecklistFieldType.enum';
import { TextFieldData } from '../../checklist/types/ChecklistFieldDataTypes';
import { parseCustomInputContent } from './custom-inputs/custom-input.parser';
import { FileRef } from './file-upload/file-upload.types';
import { getItemTitleFallback } from '../../checklist/utils/item-title.util';
import { VspRegulationLookupValue } from './vsp-regulation-lookup/vsp-regulation-lookup.component';
import { ShipsanRegulationLookupValue } from './shipsan-regulation-lookup/shipsan-regulation-lookup.component';
import { parseJsonObjectValue } from './json-object-value';
import { parseDateTimeInputValue } from './date-time-input/date-time-input.value';

function isNonNull<T>(v: T | null | undefined): v is T {
  return v != null;
}

export const CUSTOM_INPUT_ID_PREFIX = 'custom-input-';

interface ProjectedField {
  position: number;
  definition: FieldDefinition;
}

function mapField(f: ChecklistItemField): FieldDefinition<string> | null {
  if (f.type === ChecklistFieldType.Text) {
    const data = f.data as TextFieldData;
    return {
      id: `field_${f.id}`,
      type: 'text-readonly',
      label: 'Instructions',
      value: data.text ?? '',
      readOnly: true,
      props: {
        containerClass: 'flex flex-col gap-1.5',
      },
    };
  } else {
    return null;
  }
}

function mapCustomInput(
  customInput: ChecklistItemCustomInput,
  canEdit: boolean
): FieldDefinition | null {
  const parsed = parseCustomInputContent(
    customInput.customInputTypeId,
    customInput.inputContent
  );
  const metadata = {
    customInput: {
      id: customInput.id,
      itemId: customInput.itemId,
      typeId: customInput.customInputTypeId,
      typeName: parsed.type,
      requiredInput: customInput.requiredInput ?? false,
      slotId: customInput.slotId ?? undefined,
      templateFileId: parsed.templateFileId,
      previewFile: parsed.previewFile,
    },
  };
  const validators = customInput.requiredInput
    ? [{ name: 'required' }]
    : undefined;
  const hideLabel = parsed.hideFieldHeader || undefined;

  switch (parsed.type) {
    case 'instruction': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'text-readonly',
        label: 'Instructions',
        value: parsed.label || parseInstructionText(customInput.inputContent),
        readOnly: true,
        props: {
          containerClass: 'flex flex-col gap-1.5',
        },
        metadata,
      };
    }

    case 'string_field': {
      const placeholder = canEdit ? 'Write here...' : 'No input added to task.';
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: parsed.answerType === 'long' ? 'textarea' : 'text-input',
        label: parsed.label || 'Text input',
        value: parseStringInputValue(customInput.payload),
        readOnly: !canEdit,
        validators,
        props:
          parsed.answerType === 'long'
            ? {
                containerClass: 'flex flex-col gap-1.5',
                placeholder,
                rows: 5,
                textareaClass: 'remarks-textarea',
              }
            : {
                containerClass: 'flex flex-col gap-1.5',
                hideLabel,
                placeholder,
              },
        metadata,
      };
    }

    case 'number_input': {
      const numberValue = parseNumberInputValue(customInput.payload);

      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'number-input',
        label: parsed.label || 'Number input',
        value: numberValue,
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          placeholder: canEdit ? '0' : 'No input added to task.',
          suffixText: parsed.unit,
        },
        metadata,
      };
    }

    case 'checkbox': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'checkbox-group',
        label: parsed.label || 'Checkbox Group',
        value: parseCheckboxValue(customInput.payload),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          options: parsed.elements,
        },
        metadata,
      };
    }

    case 'dropdown': {
      const dropdownOptions =
        parsed.options.length > 0
          ? parsed.options
          : parsed.elements.map(option => ({ label: option, value: option }));

      if (parsed.dropdownType === 'multiple') {
        return {
          id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
          type: 'dropdown-multiple',
          label: parsed.label || 'Select options',
          value: parseCheckboxValue(customInput.payload),
          readOnly: !canEdit,
          validators,
          props: {
            containerClass: 'flex flex-col gap-1.5',
            hideLabel,
            options: dropdownOptions,
            placeholder: 'Select one or more...',
            multiple: true,
            tagSelection: true,
          },
          metadata,
        };
      }

      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'dropdown',
        label: parsed.label || 'Select option',
        value: parseSingleSelectValue(customInput.payload),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          options: dropdownOptions,
          placeholder: 'Select an option...',
        },
        metadata,
      };
    }

    case 'date_time': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'date-time-input',
        label: parsed.label || 'Date and time',
        value: parseDateTimeInputValue(
          parseStringInputValue(customInput.payload)
        ),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          placeholder: canEdit ? '' : 'No input added to task.',
          dateField: parsed.dateField,
          timeField: parsed.timeField,
        },
        metadata,
      };
    }

    case 'divider': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'divider',
        label: parsed.dividerHeader ? parsed.label : '',
        readOnly: true,
        metadata: {
          customInput: { ...metadata.customInput, requiredInput: false },
        },
      };
    }

    case 'vsp_regulation_lookup': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'vsp-regulation-lookup',
        label: parsed.label || 'VSP regulation lookup',
        value: parseJsonObjectValue<
          VspRegulationLookupValue | VspRegulationLookupValue[]
        >(customInput.payload),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
        },
        metadata,
      };
    }

    case 'shipsan_regulation_lookup': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'shipsan-regulation-lookup',
        label: parsed.label || 'ShipSan regulation lookup',
        value: parseJsonObjectValue<
          ShipsanRegulationLookupValue | ShipsanRegulationLookupValue[]
        >(customInput.payload),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
        },
        metadata,
      };
    }

    case 'radiobutton': {
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'radio-input',
        label: parsed.label || 'Select option',
        value: parseSingleSelectValue(customInput.payload),
        readOnly: !canEdit,
        validators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          options: parsed.elements,
        },
        metadata,
      };
    }

    case 'image_upload': {
      const imageValidators = customInput.requiredInput
        ? [{ name: 'requiredFile' }]
        : undefined;
      const parsedFiles = parseFilesFromPayload(customInput.payload);
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'image-upload',
        label: parsed.label || 'Image upload',
        value: parsedFiles,
        readOnly: !canEdit,
        validators: imageValidators,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          hideLabel,
          maxFiles: parsed.maxFiles,
        },
        metadata,
      };
    }

    default:
      return {
        id: `${CUSTOM_INPUT_ID_PREFIX}${customInput.id}`,
        type: 'unsupported-field',
        label: parsed.label || 'Unsupported field',
        readOnly: true,
        props: {
          containerClass: 'flex flex-col gap-1.5',
          message: 'This field is not supported by the current version.',
        },
        metadata,
      };
  }
}

function parseInstructionText(inputContent?: string | null): string {
  if (!inputContent) return '';

  const trimmed = inputContent.trim();
  if (!trimmed) return '';

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') {
      return parsed;
    }
    if (parsed && typeof parsed === 'object') {
      if (typeof parsed.label === 'string') {
        return parsed.label;
      }
      if (typeof parsed.content === 'string') {
        return parsed.content;
      }
    }
  } catch {
    // Input content is not JSON, so fall back to the original value.
  }

  return trimmed;
}

export function parseStringInputValue(payload?: unknown): string {
  if (payload == null) return '';

  if (typeof payload === 'object') {
    const directValue = extractValueFromObject(
      payload as Record<string, unknown>
    );
    if (directValue != null) return directValue;
    payload = JSON.stringify(payload);
  }

  if (typeof payload !== 'string') return '';

  const trimmed = payload.trim();
  if (!trimmed || trimmed === 'null') return '';

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') return parsed;
    if (typeof parsed === 'object' && parsed !== null) {
      const val = extractValueFromObject(parsed as Record<string, unknown>);
      if (val != null) return val;
    } else {
      return trimmed;
    }
  } catch {
    return trimmed;
  }

  return '';
}

export function parseNumberInputValue(payload?: unknown): number | null {
  if (payload == null) return null;

  if (typeof payload === 'number') {
    return Number.isFinite(payload) ? payload : null;
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const directValue = record['value'];
    if (typeof directValue === 'number') {
      return Number.isFinite(directValue) ? directValue : null;
    }
    if (typeof directValue === 'string') {
      return parseDecimalString(directValue);
    }
    payload = JSON.stringify(payload);
  }

  if (typeof payload !== 'string') return null;

  const trimmed = payload.trim();
  if (!trimmed || trimmed === 'null') return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'number') {
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (parsed && typeof parsed === 'object') {
      const value = (parsed as Record<string, unknown>)['value'];
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === 'string') {
        return parseDecimalString(value);
      }
    }
  } catch {
    return parseDecimalString(trimmed);
  }

  return null;
}

function parseDecimalString(value: string): number | null {
  const canonical = value.trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$|^-?\.\d+$/.test(canonical)) return null;
  const parsed = Number.parseFloat(canonical);
  return Number.isFinite(parsed) ? parsed : null;
}

function extractValueFromObject(obj: Record<string, unknown>): string | null {
  const value = obj['value'];
  if (typeof value === 'string') return value;
  const payload = obj['payload'];
  if (
    payload &&
    typeof payload === 'object' &&
    typeof (payload as Record<string, unknown>)['value'] === 'string'
  ) {
    return (payload as Record<string, string>)['value'] ?? '';
  }
  return null;
}

export function parseCheckboxValue(payload?: unknown): string[] {
  if (payload == null) return [];

  if (Array.isArray(payload)) {
    return normalizeSelectionArray(payload);
  }

  if (typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj['selected'])) {
      return normalizeSelectionArray(obj['selected']);
    }
    if (
      obj['payload'] &&
      typeof obj['payload'] === 'object' &&
      Array.isArray((obj['payload'] as Record<string, unknown>)['selected'])
    ) {
      return normalizeSelectionArray(
        (obj['payload'] as Record<string, unknown>)['selected'] as unknown[]
      );
    }
  }

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed || trimmed === 'null') return [];
    try {
      const parsed = JSON.parse(trimmed);
      return parseCheckboxValue(parsed);
    } catch {
      return [];
    }
  }

  return [];
}

function normalizeSelectionArray(values: unknown[]): string[] {
  return values
    .filter((v): v is string => typeof v === 'string')
    .map(v => v.trim())
    .filter(v => v.length > 0);
}

export function parseSingleSelectValue(payload?: unknown): string | null {
  if (payload == null) return null;

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed || trimmed === 'null') return null;
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'string' || typeof parsed === 'object'
        ? parseSingleSelectValue(parsed)
        : trimmed;
    } catch {
      return trimmed;
    }
  }

  if (typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (typeof obj['selected'] === 'string') {
      return obj['selected'];
    }
    if (
      obj['payload'] &&
      typeof obj['payload'] === 'object' &&
      typeof (obj['payload'] as Record<string, unknown>)['selected'] ===
        'string'
    ) {
      return (obj['payload'] as Record<string, string>)['selected'];
    }
  }

  return null;
}

export function parseFilesFromPayload(payload?: unknown): FileRef[] {
  if (payload == null) return [];

  let obj: Record<string, unknown>;
  if (typeof payload === 'string') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      return [];
    }
    if (parsed === null || typeof parsed !== 'object') return [];
    obj = parsed as Record<string, unknown>;
  } else if (typeof payload === 'object') {
    obj = payload as Record<string, unknown>;
  } else {
    return [];
  }

  const files = Array.isArray(obj['files']) ? obj['files'] : [];
  return files.filter(
    (f): f is FileRef =>
      f != null &&
      typeof f === 'object' &&
      typeof (f as Record<string, unknown>)['id'] === 'string' &&
      typeof (f as Record<string, unknown>)['fileName'] === 'string' &&
      typeof (f as Record<string, unknown>)['contentType'] === 'string'
  );
}

// Deliberately no `metadata.customInput` because the form facade would save it through the custom-input endpoint.
function generateHeaderField(item: ChecklistItem): FieldDefinition<string> {
  return {
    id: 'header',
    type: 'text-input',
    label: 'Title',
    value: item.header ?? '',
    readOnly: false,
    validators: [{ name: 'required' }],
    props: {
      containerClass: 'flex flex-col gap-1.5',
      placeholder: getItemTitleFallback(item),
    },
  };
}

function generateRemarksField(
  item: ChecklistItem,
  canEdit: boolean
): FieldDefinition<string> {
  return {
    id: 'remarks',
    type: 'remarks',
    label: 'Remarks',
    value: item.remarks ?? '',
    readOnly: !canEdit,
    props: {
      placeholder: canEdit ? 'Write here...' : 'No remarks added to task.',
      rows: 5,
      containerClass: 'flex flex-col h-[10rem] gap-2',
      textareaClass: 'remarks-textarea',
    },
  };
}

function generateCompletionTimeField(
  item: ChecklistItem,
  canEdit: boolean
): FieldDefinition<string> {
  return {
    id: 'completionTime',
    type: 'datetime',
    label: 'Task completion time',
    value: item.completionTime ?? undefined,
    readOnly: !canEdit,
    props: {
      mode: 'datetime',
      allowTzChange: true,
      stepMinutes: 1,
      containerClass: 'flex flex-col gap-1.5',
      // Only a manual edit (entry_time) is flagged as edited, while an undo-preserved time is not.
      edited: !!item.entryTime,
    },
  };
}

export function projectItemToFields(
  item: ChecklistItem,
  status: ChecklistStatus
): FieldDefinition[] {
  const canEdit =
    status === ChecklistStatus.InProgress &&
    item.status === ChecklistItemStatus.Unchecked;

  const legacyFields = item.fields
    .map(field => projectField(field.position, mapField(field)))
    .filter(isNonNull);
  const customInputFields = (item.customInputs ?? [])
    .map(customInput =>
      projectField(customInput.position, mapCustomInput(customInput, canEdit))
    )
    .filter(isNonNull);
  const base: FieldDefinition[] = [...legacyFields, ...customInputFields]
    .sort((a, b) => a.position - b.position)
    .map(projected => projected.definition);

  // Remarks is NOT part of item.fields or customInputs, and it is appended as its own field.
  const remarksField = generateRemarksField(item, canEdit);
  const completionTimeFiled = generateCompletionTimeField(item, canEdit);

  const showEditableHeader =
    item.editableHeader === true && !item.isRepeatablePrototype && canEdit;
  const headerField = showEditableHeader ? generateHeaderField(item) : null;

  return [
    ...(headerField ? [headerField] : []),
    ...base,
    remarksField,
    completionTimeFiled,
  ];
}

function projectField(
  position: number,
  definition: FieldDefinition | null
): ProjectedField | null {
  if (!definition) return null;
  return { position, definition };
}
